import React from 'react';
import {
  Camera,
  Download,
  Eraser,
  Frame,
  Moon,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Sun
} from 'lucide-react';
import { EXPORT_FORMATS } from '../utils/exporters';

export default function Toolbar({
  onExport,
  onScreenshot,
  onResetCamera,
  onClearScene,
  bgColor,
  onBgColorChange,
  hasModel,
  theme,
  leftPanelCollapsed,
  rightPanelCollapsed,
  onThemeToggle,
  onToggleLeftPanel,
  onToggleRightPanel
}) {
  const [exportFormat, setExportFormat] = React.useState('stl');

  return (
    <header className="app-bar">
      <div className="brand-lockup">
        <div className="brand-mark">C</div>
        <div>
          <h1>Cadify</h1>
          <span>Lightweight browser CAD</span>
        </div>
      </div>

      <div className="command-bar">
        <div className="layout-command-group">
          <button
            onClick={onToggleLeftPanel}
            className="icon-btn with-tooltip"
            title={leftPanelCollapsed ? 'Show creation panel' : 'Hide creation panel'}
            aria-label={leftPanelCollapsed ? 'Show creation panel' : 'Hide creation panel'}
            data-tooltip={leftPanelCollapsed ? 'Show the create/import panel' : 'Hide the create/import panel for more canvas space'}
          >
            {leftPanelCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>

          <button
            onClick={onToggleRightPanel}
            className="icon-btn with-tooltip"
            title={rightPanelCollapsed ? 'Show inspector panel' : 'Hide inspector panel'}
            aria-label={rightPanelCollapsed ? 'Show inspector panel' : 'Hide inspector panel'}
            data-tooltip={rightPanelCollapsed ? 'Show the inspector and dimensions panel' : 'Hide the inspector for more canvas space'}
          >
            {rightPanelCollapsed ? <PanelRightOpen size={17} /> : <PanelRightClose size={17} />}
          </button>
        </div>

        <div className="export-group">
          <select
            value={exportFormat}
            onChange={(event) => setExportFormat(event.target.value)}
            disabled={!hasModel}
            aria-label="Export format"
          >
            {Object.entries(EXPORT_FORMATS).map(([key, format]) => (
              <option key={key} value={key}>
                {format.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => onExport(exportFormat)}
            disabled={!hasModel}
            className={`btn with-tooltip ${hasModel ? 'btn-primary' : 'btn-disabled'}`}
            title="Export model"
            data-tooltip={hasModel ? `Export the scene as ${exportFormat.toUpperCase()}` : 'Add or import a model before exporting'}
          >
            <Download size={16} />
            Export
          </button>
        </div>

        <button
          onClick={onResetCamera}
          className="icon-btn with-tooltip"
          title="Frame all"
          data-tooltip="Fit all models into the viewport"
        >
          <Frame size={17} />
        </button>

        <button
          onClick={onScreenshot}
          className="icon-btn with-tooltip"
          title="Screenshot"
          data-tooltip="Save a PNG screenshot of the canvas"
        >
          <Camera size={17} />
        </button>

        <button
          onClick={onClearScene}
          disabled={!hasModel}
          className={`icon-btn danger with-tooltip ${!hasModel ? 'disabled' : ''}`}
          title="Clear scene"
          data-tooltip={hasModel ? 'Remove every object from the scene' : 'No objects to clear'}
        >
          <Eraser size={17} />
        </button>

        <label
          className="color-command with-tooltip"
          title="Workspace color"
          data-tooltip="Change the canvas background color"
        >
          <Palette size={16} />
          <input
            type="color"
            value={bgColor}
            onChange={(event) => onBgColorChange(event.target.value)}
            aria-label="Workspace color"
          />
        </label>

        <button
          onClick={onThemeToggle}
          className="icon-btn with-tooltip"
          title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          data-tooltip={theme === 'light' ? 'Switch Cadify to dark theme' : 'Switch Cadify to light theme'}
        >
          {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
        </button>
      </div>
    </header>
  );
}
